// FabricationOrderAcceptanceListContainer — MOD acceptance screen, laid out like the approved
// prototype: a fabrication order list on the left and, when a fabrication order is selected, its
// acceptance form on the right. Server-side paging + search-by-identification (the FO catalogue is
// large). Status changes are reflected by PULL (S18, D7): on mount, refresh, page change, (debounced)
// search, and after a circuit action (start/finish).
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, InputAdornment, Pagination, TextField, Typography } from '@mui/material'
import { SearchRounded } from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { apiGet, apiPost } from '../generic/Api_Functions'
import FabricationOrderAcceptanceList from './FabricationOrderAcceptanceList'
import FabricationOrderAcceptanceForm from './FabricationOrderAcceptanceForm'

const FABRICATION_ORDER_ACCEPTANCE_BASE = 'fabricationOrderAcceptance'
const FABRICATION_ORDER_ACCEPTANCE_LIST_ENDPOINT = `${FABRICATION_ORDER_ACCEPTANCE_BASE}/list`
const PAGE_SIZE = 10
const SEARCH_DEBOUNCE_MS = 400

export default function FabricationOrderAcceptanceListContainer () {
  const { t_ } = useSmartTranslation()
  const [fabricationOrders, setFabricationOrders] = useState([])
  const [page, setPage] = useState(0) // 0-based, mirrors Sun
  const [totalPages, setTotalPages] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [selectedId, setSelectedId] = useState(null)

  // The selected fabrication order is always derived from the latest list data, so its status (and
  // thus the form's actions) stay in sync after a refresh / circuit action.
  const selectedFabricationOrder = useMemo(
    () => fabricationOrders.find((fabricationOrder) => fabricationOrder.fabricationOrderId === selectedId),
    [fabricationOrders, selectedId]
  )

  const loadFabricationOrders = useCallback(() => {
    const params = `?page=${page}&size=${PAGE_SIZE}&search=${encodeURIComponent(appliedSearch)}`
    apiGet(`${FABRICATION_ORDER_ACCEPTANCE_LIST_ENDPOINT}${params}`).then((result) => {
      setFabricationOrders((result && result.content) || [])
      setTotalPages((result && result.totalPages) || 0)
    })
  }, [page, appliedSearch])

  // CU2 (#70) — start: opens/resumes the active PENDING version or, after a FAILED close, creates the
  // next one (Sun decides from the current status). Then re-queries (pull) so the badge, result and
  // attempt number reflect the change.
  const startAcceptance = useCallback((fabricationOrderId) => {
    apiPost(`${FABRICATION_ORDER_ACCEPTANCE_BASE}/${fabricationOrderId}/start`, '').then(loadFabricationOrders).catch(() => {})
  }, [loadFabricationOrders])

  // CU4 (#70) — close the active version with its result (PASSED/FAILED) and the operator's on-screen
  // form. C7 (#70 r3) — the full form is sent so Sun persists+freezes exactly what was on screen at
  // close time (not a stale debounced autosave); the frozen version, and any version that inherits it,
  // stay faithful. `reopen` was removed: a closed version is immutable (correct it with a new version).
  const finishAcceptance = useCallback((fabricationOrderId, result, form) => {
    apiPost(`${FABRICATION_ORDER_ACCEPTANCE_BASE}/${fabricationOrderId}/finish`, { result, form }).then(loadFabricationOrders).catch(() => {})
  }, [loadFabricationOrders])

  // C2/S31 (#70 r3) — interrupt (pause) the active version, saving its form content, without creating a
  // new version. Sun moves the attempt to INTERRUPTED (paused); re-query so the screen shows it paused
  // (fields read-only) with "Resume acceptance" available to resume.
  const interruptAcceptance = useCallback((fabricationOrderId, formData) => {
    apiPost(`${FABRICATION_ORDER_ACCEPTANCE_BASE}/${fabricationOrderId}/interrupt`, formData).then(loadFabricationOrders).catch(() => {})
  }, [loadFabricationOrders])

  useEffect(() => {
    loadFabricationOrders()
  }, [loadFabricationOrders])

  // Debounce the search box: after the user stops typing, apply the term and jump back to page 0.
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0)
      setAppliedSearch(searchInput)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput])

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', px: 1.5, pt: 1, pb: 1.5 }}>
        <TextField
          size='small'
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder={t_('Search fabrication order')}
          inputProps={{ 'aria-label': t_('Search fabrication order') }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchRounded fontSize='small' />
              </InputAdornment>
            )
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', px: 1.5, pb: 1.5 }}>
        <Box>
          <FabricationOrderAcceptanceList
            fabricationOrders={fabricationOrders}
            selectedId={selectedId}
            onSelect={(fabricationOrder) => setSelectedId(fabricationOrder.fabricationOrderId)}
          />
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <Pagination count={totalPages} page={page + 1} onChange={(event, value) => setPage(value - 1)} size='small' color='primary' />
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {selectedFabricationOrder
            ? <FabricationOrderAcceptanceForm fabricationOrder={selectedFabricationOrder} onStart={startAcceptance} onFinish={finishAcceptance} onInterrupt={interruptAcceptance} onReload={loadFabricationOrders} />
            : <Typography sx={{ color: 'text.secondary', p: 2 }}>{t_('Select a fabrication order to fill the acceptance form')}</Typography>}
        </Box>
      </Box>
    </Box>
  )
}
